import React, { useState } from 'react';
import { Plus, ListTodo, Loader2, AlertCircle, Play, Sparkles } from 'lucide-react';
import { useTodos, useProcessAllTodos } from '../helpers/useTodosApi';
import { Button } from './Button';
import { TodoItem } from './TodoItem';
import { CreateTodoForm } from './CreateTodoForm';
import { Skeleton } from './Skeleton';
import { useQueryClient } from '../lib/queryClient';
import styles from './TodoListPanel.module.css';

interface TodoListPanelProps {
  sessionId: string;
  className?: string;
  onExecuteTask?: (taskId: string, taskTitle: string, taskDescription?: string) => void; // Callback to execute task with AI
  onExecuteSuggestion?: (suggestion: string) => void; // Execute suggestion via AI chat
}

export const TodoListPanel: React.FC<TodoListPanelProps> = ({
  sessionId,
  className,
  onExecuteTask,
  onExecuteSuggestion,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const { data, isLoading, error, refetch } = useTodos(sessionId);
  const { mutate: processAll, isPending: isProcessing } = useProcessAllTodos();
  const queryClient = useQueryClient();

  const todos = data?.todos || [];
  const incompleteTodos = todos.filter((t) => !t.isCompleted);
  const hasUnprocessedTodos = incompleteTodos.some(
    (t) => !t.generationStatus || t.generationStatus === 'pending',
  );
  const processingCount = todos.filter((t) => t.generationStatus === 'processing').length;

  const handleProcessAll = () => {
    if (!sessionId) return;
    processAll({ sessionId });
  };

  // Sort todos: Incomplete first, then by due date (earliest first), then created date
  const sortedTodos = [...todos].sort((a, b) => {
    if (a.isCompleted === b.isCompleted) {
      // If both completed or both incomplete
      if (a.dueDate && b.dueDate) {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
      if (a.dueDate) return -1; // a has due date, comes first
      if (b.dueDate) return 1; // b has due date, comes first

      // Fallback to creation date
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    }
    // Incomplete comes first
    return a.isCompleted ? 1 : -1;
  });

  return (
    <div className={`${styles.panel} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <ListTodo className={styles.headerIcon} />
          <h2>To-Do List</h2>
          <span className={styles.countBadge}>{incompleteTodos.length}</span>
        </div>
        <div className={styles.headerActions}>
          {incompleteTodos.length > 0 && (
            <Button
              size="sm"
              variant={isProcessing ? 'secondary' : 'outline'}
              onClick={handleProcessAll}
              disabled={isProcessing}
              className={styles.processAllBtn}
              title="Process all todos with AI"
            >
              {isProcessing || processingCount > 0 ? (
                <>
                  <Loader2 size={14} className={styles.spinnerIcon} />
                  {processingCount > 0 ? `Processing ${processingCount}...` : 'Starting...'}
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Process All
                </>
              )}
            </Button>
          )}
          <Button
            size="icon-sm"
            variant={isCreating ? 'secondary' : 'ghost'}
            onClick={() => setIsCreating(!isCreating)}
            aria-label="Add new task"
            className={styles.addButton}
          >
            <Plus size={18} />
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        {isCreating && (
          <CreateTodoForm
            sessionId={sessionId}
            onCancel={() => setIsCreating(false)}
            onSuccess={() => setIsCreating(false)}
          />
        )}

        {isLoading ? (
          <div className={styles.loadingState}>
            <Skeleton className={styles.skeletonItem} />
            <Skeleton className={styles.skeletonItem} />
            <Skeleton className={styles.skeletonItem} />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <AlertCircle size={32} className={styles.errorIcon} />
            <p>Failed to load todos</p>
            <p className={styles.errorMessage}>
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : sortedTodos.length === 0 && !isCreating ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrapper}>
              <ListTodo size={32} />
            </div>
            <h3>All caught up!</h3>
            <p>No tasks pending for this session.</p>
            <Button variant="outline" size="sm" onClick={() => setIsCreating(true)}>
              Create your first task
            </Button>
          </div>
        ) : (
          <div className={styles.list}>
            {sortedTodos.map((todo) => (
              <TodoItem
                key={todo.id}
                todo={todo}
                onExecuteTask={onExecuteTask}
                onExecuteSuggestion={onExecuteSuggestion}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
